import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ProductDetailModal = ({ product, onClose, onUpdate, onDelete }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedProduct, setEditedProduct] = useState({ ...product });
   const [selectedImageFile, setSelectedImageFile] = useState(null);
   const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const token = localStorage.getItem("adminToken");


  const handleSave = async () => {
    setIsSaving(true);
    try {
      let imageUrl = editedProduct.img;
  
      // Upload new image if selected
      if (selectedImageFile) {
        const formData = new FormData();
        formData.append('file', selectedImageFile);
        formData.append('upload_preset', 'africfreshupload');
  
        const cloudinaryResponse = await axios.post(
          'https://api.cloudinary.com/v1_1/dfehxeb5r/image/upload',
          formData
        );
        imageUrl = cloudinaryResponse.data.secure_url;
      }
  
      // Update product with new image URL
      const updatedProduct = {
        ...editedProduct,
        img: imageUrl
      };
  
      const response = await axios.put(
        `https://afric-fresh-backend.onrender.com/admin/update/${product._id}`,
        updatedProduct,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      onUpdate(response.data.product);
      setEditMode(false);
      setSelectedImageFile(null);
      toast.success('Product updated successfully!');
    } catch (error) {
      console.error('Failed to update product', error);
      toast.error('Failed to update product');
    } finally {
      setIsSaving(false);
    }
  };
  

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(
          `https://afric-fresh-backend.onrender.com/admin/delete/${product._id}`
          // `http://localhost:3005/admin/delete/${product._id}`
          ,{
            headers: {
              Authorization: `Bearer ${token}`
            }});
        onDelete(product._id);
        onClose();
        toast.success('Product deleted successfully!');
      } catch (error) {
        console.error('Failed to delete product', error);
        toast.error('Failed to delete product');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {editMode ? 'Edit Product' : 'Product Details'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        <div className="space-y-4">
        <div className="flex justify-center mb-4">
            <img
              src={
                editMode && selectedImageFile
                  ? URL.createObjectURL(selectedImageFile)
                  : editedProduct.img || '/placeholder-image.png'
              }
              alt={editedProduct.name}
              className="w-64 h-64 object-cover rounded-lg"
            />
          </div>

          {editMode && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Change Product Image
              </label>
              <input
                type="file"
                onChange={(e) => setSelectedImageFile(e.target.files[0])}
                accept="image/*"
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {[{ label: 'Name', name: 'name', type: 'text' },
              { label: 'Brand', name: 'brand', type: 'text' },
              { label: 'Price', name: 'price', type: 'text' },
              { label: 'Category', name: 'category', type: 'text' },
              { label: 'Quantity', name: 'quantity', type: 'number' }].map((field) => (
              <div key={field.name} className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                {editMode ? (
                  <input
                    type={field.type}
                    name={field.name}
                    value={editedProduct[field.name] || ''}
                    onChange={handleInputChange}
                    className="border rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-gray-900">{editedProduct[field.name] || 'N/A'}</p>
                )}
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">Description</label>
            {editMode ? (
              <textarea
                name="description"
                value={editedProduct.description || ''}
                onChange={handleInputChange}
                className="w-full border rounded px-2 py-1 h-24"
              />
            ) : (
              <p className="text-gray-900">{editedProduct.description || 'No description available'}</p>
            )}
          </div>

          <div className="flex justify-between mt-4">
          {editMode ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-green-300 flex items-center gap-2"
              >
                {isSaving && (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditMode(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Edit Product
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Delete Product
              </button>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

const AdminProduct = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('available'); // available | outOfStock

  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          'https://afric-fresh-backend.onrender.com/admin/products'
          ,{
            headers: {
              Authorization: `Bearer ${token}`
            }}
        );
        const productsData = response.data.products || response.data || [];
        setProducts(productsData);
        setFilteredProducts(productsData);
        setAvailableProducts(productsData.filter((product) => product.quantity > 0));
        setOutOfStockProducts(productsData.filter((product) => product.quantity <= 0));
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch products', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const currentProducts = currentView === 'available' ? availableProducts : outOfStockProducts;
    setFilteredProducts(
      currentProducts.filter((product) =>
        product.name.toLowerCase().includes(query)
      )
    );
  };

  const handleUpdateProduct = (updatedProduct) => {
    setProducts((prev) =>
      prev.map((product) =>
        product._id === updatedProduct._id ? updatedProduct : product
      )
    );

    const isAvailable = updatedProduct.quantity > 0;
    const updatedAvailable = isAvailable
      ? [...availableProducts.filter((p) => p._id !== updatedProduct._id), updatedProduct]
      : availableProducts.filter((p) => p._id !== updatedProduct._id);

    const updatedOutOfStock = !isAvailable
      ? [...outOfStockProducts.filter((p) => p._id !== updatedProduct._id), updatedProduct]
      : outOfStockProducts.filter((p) => p._id !== updatedProduct._id);

    setAvailableProducts(updatedAvailable);
    setOutOfStockProducts(updatedOutOfStock);

    setFilteredProducts((prev) =>
      prev.map((product) =>
        product._id === updatedProduct._id ? updatedProduct : product
      )
    );
    setSelectedProduct(updatedProduct);
  };

  const handleDeleteProduct = (deletedProductId) => {
    setProducts((prev) => prev.filter((product) => product._id !== deletedProductId));
    setAvailableProducts((prev) =>
      prev.filter((product) => product._id !== deletedProductId)
    );
    setOutOfStockProducts((prev) =>
      prev.filter((product) => product._id !== deletedProductId)
    );
    setFilteredProducts((prev) =>
      prev.filter((product) => product._id !== deletedProductId)
    );
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 mt-10">Error: {error}</div>
    );
  }

  const currentProducts = currentView === 'available' ? availableProducts : outOfStockProducts;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">All Products</h1>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearch}
            className="border rounded px-4 py-2 w-64"
          />
          <Link to="/admin/products/addproduct">
            <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
              Add New Product
            </button>
          </Link>
        </div>
      </div>

      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => {
            setCurrentView('available');
            setFilteredProducts(availableProducts);
          }}
          className={`px-4 py-2 rounded ${
            currentView === 'available'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800'
          }`}
        >
          Available Products
        </button>
        <button
          onClick={() => {
            setCurrentView('outOfStock');
            setFilteredProducts(outOfStockProducts);
          }}
          className={`px-4 py-2 rounded ${
            currentView === 'outOfStock'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800'
          }`}
        >
          Out of Stock Products
        </button>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center text-gray-500">No products found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <div
              key={product._id}
              className="border rounded-lg p-4 shadow-md hover:shadow-lg transition"
            >
              <img
                src={product.img || '/placeholder-image.png'}
                alt={product.name}
                className="w-full h-48 object-cover mb-4"
              />
              <h2 className="text-lg font-semibold">{product.name}</h2>
              <div className="flex justify-between items-center mt-2">
                <span className="text-green-600 font-bold">
                  ${product.price}
                </span>
                <span className="text-gray-500">Stock: {product.quantity}</span>
              </div>
              <button
                onClick={() => setSelectedProduct(product)}
                className="w-full mt-4 bg-blue-500 text-white py-2 rounded hover:bg-blue-600"
              >
                View Details
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onUpdate={handleUpdateProduct}
          onDelete={handleDeleteProduct}
        />
      )}
    </div>
  );
};

export default AdminProduct;
